/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from '@azure/msal-browser';

// Add here the endpoints for MS Graph API services you would like to use.
export const graphConfig = {
    graphMeEndpoint: "https://graph.microsoft.com/v1.0/me?$select=companyName,jobTitle,id,displayName,givenName,surname,mail,email,otherMails,userPrincipalName",
    graphMailEndpoint: "https://graph.microsoft.com/v1.0/me/messages",
    graphMePhotoEndpoint: "https://graph.microsoft.com/v1.0/me/photo/$value",
    graphUsersEndpoint: userId => `https://graph.microsoft.com/v1.0/users/${userId}`
};

export const protectedResources = {
    user: {
        scopes: {
            read: [
                "User.Read"
            ]
        }
    },
    apiHeartBeats: {
        endpoint: 'http://localhost:5000/api/heartbeats',
        scopes: {
            send: [
                'api://property-manager/HeartBeat.Send'
            ],
        }
    },
    signalR: {
        endpoint: 'http://localhost:5000/api/signalr',
        scopes: {
            use: [
                'api://property-manager/SignalR.Use'
            ],
        }
    },
    apiMenu: {
        endpoint: 'http://localhost:5000/api/menu',
        scopes: {
            read: [
                'api://property-manager/Menu.List'
            ],
        }
    },
    apiCalendar: {
        endpoint: 'http://localhost:5000/api/calendar',
        scopes: {
            read: [
                'api://property-manager/Calendar.Read',
                'api://property-manager/Calendar.List'
            ],
            write: ['api://property-manager/Calendar.Write'],
        }
    },
    apiKanBan: {
        endpoint: 'http://localhost:5000/api/kanban',
        scopes: {
            read: [
                'api://property-manager/Kanban.Read',
                'api://property-manager/Kanban.List'
            ],
            write: ['api://property-manager/Kanban.Write'],
        }
    },
    apiCompany: {
        endpoint: 'http://localhost:8082/api/companies',
        scopes: {
            read: [
                'api://property-manager/Company.Read',
                'api://property-manager/Company.List'
            ],
            write: ['api://property-manager/Company.Write'],
            readWrite: [
                'api://property-manager/Company.Read',
                'api://property-manager/Company.List',
                'api://property-manager/Company.Write'
            ]
        }
    },
    apiProperty: {
        endpoint: 'http://localhost:7080/api/property',
        scopes: {
            read: [
                'api://property-manager/Property.Read',
                'api://property-manager/Property.List'
            ],
            write: ['api://property-manager/Property.Write'],
        }
    },
    apiXero: {
        endpoint: 'http://localhost:7080/api/xero',
        scopes: {
            read: [
                'api://property-manager/Xero.Read',
                'api://property-manager/Xero.List'
            ],
            write: ['api://property-manager/Xero.Write'],
            readWrite: [
                'api://property-manager/Xero.Read',
                'api://property-manager/Xero.List',
                'api://property-manager/Xero.Write'
            ]
        }
    },
    apiTenants: {
        endpoint: 'http://localhost:7080/api/tenants',
        scopes: {
            read: [
                'api://property-manager/Tenants.Read',
                'api://property-manager/Tenants.List'
            ],
            write: ['api://property-manager/Tenants.Write'],
            readWrite: [
                'api://property-manager/Tenants.Read',
                'api://property-manager/Tenants.List',
                'api://property-manager/Tenants.Write'
            ]
        }
    }
};

/**
 * Base configuration object to be passed to MSAL instance on creation.
 * Client/tenant IDs and redirect URIs are loaded at runtime from
 * either /config/config.local.json (for local dev) or /api/config
 * (when using Azure Function MSAL).
 */
export const localMsalConfig = {
    auth: {
        // These values are intentionally left blank and are
        // supplied at runtime from JSON / environment config.
        clientId: '',
        tenantId: '',
        redirectUri: '/',
        postLogoutRedirectUri: '/',
        navigateToLoginRequestUrl: false,
    },
    cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: true,
    },
    system: {
        loggerOptions: {
            logLevel: LogLevel.Error,
            loggerCallback: (level, message, containsPii) => {
                if (containsPii) {
                    return;
                }
                switch (level) {
                    case LogLevel.Error:
                        console.error(message);
                        return;
                    case LogLevel.Info:
                        console.info(message);
                        return;
                    case LogLevel.Verbose:
                        console.debug(message);
                        return;
                    case LogLevel.Warning:
                        console.warn(message);
                        return;
                    default:
                        return;
                }
            },
        },
    },
};


/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
    // Keep login minimal to avoid unexpected consent prompts; use incremental consent per API
    scopes: ["openid", "profile", "offline_access", ...protectedResources.user.scopes.read]
};

/**
 * An optional silentRequest object can be used to achieve silent SSO
 * between applications by providing a "login_hint" property.
 */
export const silentRequest = {
    scopes: ["openid", "profile"],
    
};